import React, { useState, useEffect } from 'react';
import { theme, fontStack, bodyFont } from '../theme';
import { FadeIn, Divider } from '../components/shared';
import churchConfig from '../churchConfig';
import { useChurchData, useChurchInfo } from '../data/ChurchDataProvider';

/* ── Resolve channel ID and fetch latest video ── */
async function resolveAndFetchLatest(handle, channelId) {
  let cId = channelId;

  // If no channel ID, try to resolve from handle
  if (!cId && handle) {
    const url = `https://www.youtube.com/@${handle}`;
    const proxies = [
      `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
      `https://corsproxy.io/?${encodeURIComponent(url)}`,
    ];
    for (const proxy of proxies) {
      try {
        const res = await fetch(proxy);
        if (!res.ok) continue;
        const html = await res.text();
        const patterns = [/"channelId":"(UC[a-zA-Z0-9_-]+)"/, /"externalId":"(UC[a-zA-Z0-9_-]+)"/, /"browseId":"(UC[a-zA-Z0-9_-]+)"/];
        for (const pat of patterns) {
          const match = html.match(pat);
          if (match) { cId = match[1]; break; }
        }
        if (cId) break;
      } catch (err) { console.warn("Resolve failed:", err.message); }
    }
  }

  // If we have a channel ID, fetch RSS for latest video
  if (cId) {
    const feedUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${cId}`;
    const proxies = [
      `https://api.allorigins.win/raw?url=${encodeURIComponent(feedUrl)}`,
      `https://corsproxy.io/?${encodeURIComponent(feedUrl)}`,
    ];
    for (const proxy of proxies) {
      try {
        const res = await fetch(proxy);
        if (!res.ok) continue;
        const text = await res.text();
        const xml = new DOMParser().parseFromString(text, "text/xml");
        const entry = xml.querySelector("entry");
        if (entry) {
          const videoId = entry.getElementsByTagName("yt:videoId")[0]?.textContent || entry.querySelector("id")?.textContent?.replace("yt:video:", "");
          const title = entry.querySelector("title")?.textContent || "";
          return { videoId, title, channelId: cId };
        }
      } catch (err) { console.warn("RSS failed:", err.message); }
    }
    // RSS failed but we have channel ID — can still use playlist embed
    return { videoId: null, title: null, channelId: cId };
  }

  return { videoId: null, title: null, channelId: null };
}

export default function HomePage({ setCurrentPage }) {
  const { data } = useChurchData();
  const info = useChurchInfo();
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [latestVideo, setLatestVideo] = useState(null);
  const [resolvedChannelId, setResolvedChannelId] = useState(info.youtubeChannelId || "");

  useEffect(() => {
    setTimeout(() => setHeroLoaded(true), 300);

    resolveAndFetchLatest(info.youtubeHandle, info.youtubeChannelId).then(result => {
      if (result.videoId) setLatestVideo({ videoId: result.videoId, title: result.title });
      if (result.channelId) setResolvedChannelId(result.channelId);
    });
  }, []);

  // For the hero background: use latest video, or playlist embed
  const heroVideoId = latestVideo?.videoId;
  const featuredTitle = latestVideo?.title || "Latest from Our Church";
  const uploadsPlaylistId = resolvedChannelId ? "UU" + resolvedChannelId.substring(2) : "";
  const addr = info.address;

  // Build featured video embed src (for the Watch section, not the hero)
  const featuredSrc = heroVideoId
    ? `https://www.youtube.com/embed/${heroVideoId}`
    : uploadsPlaylistId
    ? `https://www.youtube.com/embed/videoseries?list=${uploadsPlaylistId}`
    : null;

  return (
    <div>
      {/* ── HERO ── */}
      <section style={{ position: "relative", height: "100vh", minHeight: 600, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, zIndex: 0, background: `linear-gradient(135deg, ${theme.bg} 0%, #1a1520 50%, ${theme.bg} 100%)` }}>
          {/* Local video background — place hero-bg.mp4 in /public/ */}
          <video
            autoPlay muted loop playsInline
            style={{
              position: "absolute", top: "50%", left: "50%",
              minWidth: "100%", minHeight: "100%",
              width: "auto", height: "auto",
              transform: "translate(-50%, -50%)",
              objectFit: "cover", opacity: 0.35, pointerEvents: "none",
            }}
          >
            <source src={`${process.env.PUBLIC_URL}/hero-bg.mp4`} type="video/mp4" />
          </video>
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, rgba(10,10,15,0.4) 0%, rgba(10,10,15,0.2) 40%, rgba(10,10,15,0.6) 70%, ${theme.bg} 100%)` }} />
          <div style={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", width: 600, height: 600, background: `radial-gradient(circle, rgba(200,164,86,0.08) 0%, transparent 70%)`, pointerEvents: "none" }} />
        </div>

        <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "0 24px", maxWidth: 800 }}>
          <div style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? "translateY(0)" : "translateY(30px)", transition: "all 1.2s ease 0.2s" }}>
            <div style={{ fontFamily: bodyFont, fontSize: 12, fontWeight: 500, color: theme.gold, letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: 20 }}>Welcome Home</div>
            <h1 style={{ fontFamily: fontStack, fontSize: "clamp(40px, 8vw, 80px)", fontWeight: 300, color: theme.cream, lineHeight: 1.05, marginBottom: 24 }}>
              New Prospect<br /><span style={{ fontWeight: 600, fontStyle: "italic", color: theme.gold }}>Baptist Church</span>
            </h1>
          </div>
          <div style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? "translateY(0)" : "translateY(30px)", transition: "all 1.2s ease 0.6s" }}>
            <p style={{ fontFamily: bodyFont, fontSize: "clamp(15px, 2vw, 18px)", color: theme.textMuted, lineHeight: 1.7, maxWidth: 520, margin: "0 auto 16px" }}>{info.tagline}</p>
            <p style={{ fontFamily: bodyFont, fontSize: "clamp(13px, 1.5vw, 15px)", color: theme.textMuted, lineHeight: 1.6, maxWidth: 420, margin: "0 auto 40px", opacity: 0.8 }}>
              {addr.street}, {addr.city}, {addr.state} {addr.zip}
            </p>
          </div>
          <div style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? "translateY(0)" : "translateY(30px)", transition: "all 1.2s ease 1s", display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => { setCurrentPage("Join Us"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="btn-primary">Plan Your Visit</button>
            <button onClick={() => document.getElementById("news-section")?.scrollIntoView({ behavior: "smooth" })} className="btn-outline">Latest News</button>
          </div>
          <div style={{ opacity: heroLoaded ? 1 : 0, transition: "opacity 1.2s ease 1.4s", marginTop: 60, display: "flex", justifyContent: "center", gap: 40, flexWrap: "wrap" }}>
            {(data.heroServices || []).map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: bodyFont, fontSize: 10, color: theme.gold, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 4 }}>{s.name}</div>
                <div style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 500, color: theme.cream }}>{s.time}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ position: "absolute", bottom: 30, left: "50%", transform: "translateX(-50%)", animation: "float 2.5s ease-in-out infinite", opacity: heroLoaded ? 0.6 : 0, transition: "opacity 1s ease 2s" }}>
          <svg width="20" height="30" viewBox="0 0 20 30" fill="none"><rect x="1" y="1" width="18" height="28" rx="9" stroke={theme.gold} strokeWidth="1.5" /><circle cx="10" cy="10" r="2" fill={theme.gold}><animate attributeName="cy" values="10;18;10" dur="2s" repeatCount="indefinite" /></circle></svg>
        </div>
      </section>

      {/* ── MISSION ── */}
      <section style={{ padding: "100px 24px", textAlign: "center", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: "80%", height: 1, background: `linear-gradient(90deg, transparent, ${theme.border}, transparent)` }} />
        <FadeIn>
          <div style={{ maxWidth: 700, margin: "0 auto" }}>
            <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 20 }}>Our Mission</div>
            <h2 style={{ fontFamily: fontStack, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 300, color: theme.cream, lineHeight: 1.2, marginBottom: 12, fontStyle: "italic" }}>
              "{info.mission}"
            </h2>
            <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, marginBottom: 24 }}>{info.monthlyVerse.reference} — "{info.monthlyVerse.text.substring(0, 80)}..."</p>
            <Divider />
            <p style={{ fontFamily: bodyFont, fontSize: 16, color: theme.textMuted, lineHeight: 1.8, marginTop: 24 }}>
              Since 1861, New Prospect Baptist Church has been a beacon of hope, faith, and love in the Dry Prong community. From those first settlers gathering on horseback to our thriving congregation today, we invite you to be part of our story.
            </p>
          </div>
        </FadeIn>
      </section>

      {/* ── FEATURED VIDEO ── */}
      {featuredSrc && (
        <section style={{ padding: "0 24px 100px" }}>
          <FadeIn>
            <div style={{ maxWidth: 900, margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: 40 }}>
                <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Watch</div>
                <h2 style={{ fontFamily: fontStack, fontSize: "clamp(24px, 4vw, 40px)", fontWeight: 400, color: theme.cream }}>{featuredTitle}</h2>
              </div>
              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 4, overflow: "hidden", border: `1px solid ${theme.border}`, boxShadow: `0 20px 60px rgba(0,0,0,0.5)` }}>
                <iframe src={featuredSrc} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Latest Service" />
              </div>
            </div>
          </FadeIn>
        </section>
      )}

      {/* ── BULLETIN / NEWS ── */}
      <section id="news-section" style={{ padding: "100px 24px", position: "relative", background: `linear-gradient(180deg, ${theme.bg} 0%, ${theme.accent} 50%, ${theme.bg} 100%)` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 60 }}>
              <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Stay Informed</div>
              <h2 style={{ fontFamily: fontStack, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 400, color: theme.cream }}>Church Bulletin</h2>
              <Divider />
            </div>
          </FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24 }}>
            {data.newsItems.map((item, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div style={{
                  padding: item.featured ? 32 : 28,
                  background: item.featured ? `linear-gradient(135deg, rgba(200,164,86,0.08) 0%, ${theme.bgCard} 100%)` : theme.bgCard,
                  border: `1px solid ${item.featured ? theme.borderHover : theme.border}`,
                  transition: "all 0.4s ease", cursor: "default", position: "relative", overflow: "hidden",
                  gridColumn: item.featured ? "1 / -1" : "auto",
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = theme.gold; e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.3)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = item.featured ? theme.borderHover : theme.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  {item.featured && <div style={{ position: "absolute", top: 0, right: 0, padding: "6px 16px", background: theme.gold, fontFamily: bodyFont, fontSize: 10, fontWeight: 600, color: theme.bg, letterSpacing: "0.15em", textTransform: "uppercase" }}>This Week</div>}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <span style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.1em", textTransform: "uppercase", padding: "4px 10px", border: `1px solid ${theme.border}` }}>{item.category}</span>
                    <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted }}>{item.date}</span>
                  </div>
                  <h3 style={{ fontFamily: fontStack, fontSize: item.featured ? 26 : 22, fontWeight: 500, color: theme.cream, marginBottom: 12, lineHeight: 1.3 }}>{item.title}</h3>
                  <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, lineHeight: 1.7 }}>{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── FULL SERVICE TIMES ── */}
      <section style={{ padding: "100px 24px", background: `linear-gradient(180deg, ${theme.bg} 0%, ${theme.accent} 50%, ${theme.bg} 100%)` }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 50 }}>
              <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Join Us</div>
              <h2 style={{ fontFamily: fontStack, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 400, color: theme.cream }}>Service Times</h2>
              <Divider />
            </div>
          </FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
            {data.services.map((s, i) => (
              <FadeIn key={s.id || i} delay={i * 0.06}>
                <div style={{
                  padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center",
                  background: theme.bgCard, border: `1px solid ${theme.border}`,
                  transition: "all 0.3s ease",
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = theme.gold; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; }}
                >
                  <span style={{ fontFamily: bodyFont, fontSize: 15, color: theme.cream }}>{s.name}</span>
                  <span style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 500, color: theme.gold }}>{s.time}</span>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.4}>
            <div style={{ textAlign: "center", marginTop: 32 }}>
              <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, marginBottom: 8 }}>
                {info.address.street}, {info.address.city}, {info.address.state} {info.address.zip}
              </p>
              <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.gold }}>{info.eGiving}</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: "100px 24px", textAlign: "center" }}>
        <FadeIn>
          <div style={{ maxWidth: 600, margin: "0 auto" }}>
            <h2 style={{ fontFamily: fontStack, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 300, color: theme.cream, lineHeight: 1.2, marginBottom: 20 }}>
              You're <span style={{ fontStyle: "italic", color: theme.gold }}>Always</span> Welcome
            </h2>
            <p style={{ fontFamily: bodyFont, fontSize: 16, color: theme.textMuted, lineHeight: 1.7, marginBottom: 40 }}>
              Whether you've been walking with Christ for years or are just beginning your journey, there's a place for you at New Prospect. Call Pastor Trey at {info.phone} or visit us any Sunday.
            </p>
            <button onClick={() => { setCurrentPage("Join Us"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="btn-primary">Join Our Family</button>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
